import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Form, Input, Message, Space, Switch } from '@arco-design/web-react'
import type { OptionsAPI } from '../useOptions'
import { parseBooleanOption } from '../ratioSettingUtils'

export type JsonFieldValidator = (value: string) => string | null

export interface JsonOptionFieldDef {
  key: string
  label: string
  placeholder?: string
  extraText?: string
  validate?: JsonFieldValidator
}

export interface SwitchFieldDef {
  key: string
  label: string
  extraText?: string
}

export function formatOptionJson(raw: string): string {
  const trimmed = raw?.trim() ?? ''
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return raw ?? ''
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2)
  } catch {
    return raw ?? ''
  }
}

export function validateJsonObject(value: string): string | null {
  if (!value.trim()) return null
  try {
    const parsed = JSON.parse(value) as unknown
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return '不是合法的 JSON 对象'
    }
    return null
  } catch {
    return '不是合法的 JSON 字符串'
  }
}

export function validateJsonStringArray(value: string): string | null {
  if (!value.trim()) return null
  try {
    const parsed = JSON.parse(value) as unknown
    if (!Array.isArray(parsed)) return '必须是有效的 JSON 字符串数组，例如：["g1","g2"]'
    if (!parsed.every((item) => typeof item === 'string')) {
      return '必须是有效的 JSON 字符串数组，例如：["g1","g2"]'
    }
    const seen = new Set<string>()
    for (const item of parsed) {
      const s = String(item).trim()
      if (!s) return '数组项不能为空字符串'
      if (seen.has(s)) return `重复项：${s}`
      seen.add(s)
    }
    return null
  } catch {
    return '必须是有效的 JSON 字符串数组，例如：["g1","g2"]'
  }
}

/** 非负数字 JSON 对象：校验空 key、重复 key、负数、NaN */
export function validateJsonNumberObject(value: string, label = 'JSON'): string | null {
  const base = validateJsonObject(value)
  if (base) return base
  if (!value.trim()) return null
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>
    const seen = new Set<string>()
    for (const [k, v] of Object.entries(parsed)) {
      if (!k.trim()) return `${label} 存在空键名`
      if (seen.has(k)) return `${label} 键重复：${k}`
      seen.add(k)
      const n = typeof v === 'number' ? v : Number(v)
      if (!Number.isFinite(n)) return `${label}「${k}」须为有效数字`
      if (n < 0) return `${label}「${k}」不能为负数`
    }
    return null
  } catch {
    return `${label} 不是合法 JSON 对象`
  }
}

function defaultJsonValidator(value: string): string | null {
  return validateJsonObject(value)
}

export function useJsonOptionDraft(
  options: OptionsAPI,
  jsonKeys: string[],
  switchKeys: string[] = [],
  formatOnLoad = true
) {
  const [jsonDraft, setJsonDraft] = useState<Record<string, string>>({})
  const [switchDraft, setSwitchDraft] = useState<Record<string, boolean>>({})
  const [baselineJson, setBaselineJson] = useState<Record<string, string>>({})
  const [baselineSwitch, setBaselineSwitch] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const nextJson: Record<string, string> = {}
    jsonKeys.forEach((key) => {
      const raw = options.values[key] ?? ''
      nextJson[key] = formatOnLoad ? formatOptionJson(raw) : raw
    })
    const nextSwitch: Record<string, boolean> = {}
    switchKeys.forEach((key) => {
      nextSwitch[key] = parseBooleanOption(options.values[key] ?? '')
    })
    setJsonDraft(nextJson)
    setBaselineJson(nextJson)
    setSwitchDraft(nextSwitch)
    setBaselineSwitch(nextSwitch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.dataRevision])

  const patchJson = useCallback(
    (key: string, value: string) => {
      setJsonDraft((prev) => ({ ...prev, [key]: value }))
      options.patch({ [key]: value })
    },
    [options]
  )

  const patchSwitch = useCallback(
    (key: string, value: boolean) => {
      setSwitchDraft((prev) => ({ ...prev, [key]: value }))
      options.patch({ [key]: value })
    },
    [options]
  )

  const changedKeys = useMemo(() => {
    const keys: string[] = []
    jsonKeys.forEach((key) => {
      if ((jsonDraft[key] ?? '') !== (baselineJson[key] ?? '')) keys.push(key)
    })
    switchKeys.forEach((key) => {
      if ((switchDraft[key] ?? false) !== (baselineSwitch[key] ?? false)) keys.push(key)
    })
    return keys
  }, [baselineJson, baselineSwitch, jsonDraft, jsonKeys, switchDraft, switchKeys])

  const validateAll = useCallback(
    (fields: JsonOptionFieldDef[]) => {
      for (const field of fields) {
        const validator = field.validate ?? defaultJsonValidator
        const msg = validator(jsonDraft[field.key] ?? '')
        if (msg) return `${field.label}：${msg}`
      }
      return null
    },
    [jsonDraft]
  )

  const saveSection = useCallback(
    async (fields: JsonOptionFieldDef[]) => {
      const error = validateAll(fields)
      if (error) {
        Message.error(error)
        return false
      }
      if (changedKeys.length === 0) {
        Message.warning('你似乎并没有修改什么')
        return false
      }

      setSaving(true)
      try {
        const overrides: Record<string, string> = {}
        changedKeys.forEach((key) => {
          if (jsonKeys.includes(key)) {
            overrides[key] = jsonDraft[key] ?? ''
          } else if (switchKeys.includes(key)) {
            overrides[key] = String(switchDraft[key] ?? false)
          }
        })
        await options.save(changedKeys, overrides)
        setBaselineJson({ ...jsonDraft })
        setBaselineSwitch({ ...switchDraft })
        await options.reload()
        return true
      } catch {
        return false
      } finally {
        setSaving(false)
      }
    },
    [changedKeys, jsonDraft, jsonKeys, jsonKeys.length, options, switchDraft, switchKeys, validateAll]
  )

  return {
    jsonDraft,
    switchDraft,
    patchJson,
    patchSwitch,
    changedKeys,
    saving,
    validateAll,
    saveSection,
  }
}

interface JsonOptionSectionProps {
  options: OptionsAPI
  fields: JsonOptionFieldDef[]
  switchFields?: SwitchFieldDef[]
  saveLabel: string
  extraActions?: React.ReactNode
  /** 保存操作条位置，默认底部 sticky */
  toolbarPlacement?: 'top' | 'bottom'
  hideToolbar?: boolean
}

export function JsonOptionSection({
  options,
  fields,
  switchFields = [],
  saveLabel,
  extraActions,
  toolbarPlacement = 'bottom',
  hideToolbar = false,
}: JsonOptionSectionProps) {
  const jsonKeys = fields.map((f) => f.key)
  const switchKeys = switchFields.map((f) => f.key)
  const { jsonDraft, switchDraft, patchJson, patchSwitch, saving, saveSection } = useJsonOptionDraft(
    options,
    jsonKeys,
    switchKeys
  )

  const toolbar = hideToolbar ? null : (
    <div
      className={
        toolbarPlacement === 'bottom'
          ? 'settings-json-toolbar settings-json-toolbar--sticky'
          : 'settings-json-toolbar'
      }
    >
      <Space>
        <Button type="primary" loading={saving} onClick={() => saveSection(fields)}>
          {saveLabel}
        </Button>
        {extraActions}
      </Space>
      {saving ? <span className="settings-save-status">正在保存…</span> : null}
    </div>
  )

  return (
    <div className="settings-json-section">
      {toolbarPlacement === 'top' ? toolbar : null}
      <Form layout="vertical" className="settings-compact-form">
        {fields.map((field) => (
          <Form.Item key={field.key} label={field.label} extra={field.extraText}>
            <Input.TextArea
              value={jsonDraft[field.key] ?? ''}
              placeholder={field.placeholder}
              autoSize={{ minRows: 4, maxRows: 10 }}
              disabled={saving}
              onChange={(value) => patchJson(field.key, value)}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
            />
          </Form.Item>
        ))}
        {switchFields.map((field) => (
          <Form.Item key={field.key} label={field.label} extra={field.extraText}>
            <Switch
              checked={switchDraft[field.key] ?? false}
              disabled={saving}
              onChange={(checked) => patchSwitch(field.key, checked)}
            />
          </Form.Item>
        ))}
      </Form>
      {toolbarPlacement === 'bottom' ? toolbar : null}
    </div>
  )
}
